import React, { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import styled from "@emotion/styled";
import { useNetworkStore } from "../stores/network";
import { useMurderBoard, useCoTxs, useAddressInteractions, useRepeatedSlots } from "../api/hooks";
import {
  PageContainer,
  PageTitle,
  Card,
  Loading,
  Badge,
  Table,
  TableWrapper,
  Input,
  Button,
  Flex,
  StatCard,
  StatValue,
  StatLabel,
  Grid,
  SectionTitle,
} from "../components/ui";
import { HexDisplay } from "../components/HexDisplay";
import { theme } from "../lib/theme";
import { AddToClipboardButton } from "../components/AddToClipboardButton";
import type { MurderBoardData, PrivacyScoreFactor, MurderBoardTx } from "../lib/api";

// ── Styled components ──

const SearchBar = styled.form`
  display: flex;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.md};
`;

const AddressInput = styled(Input)`
  font-family: "SF Mono", "Fira Code", monospace;
  font-size: ${theme.fontSize.sm};
`;

const ScoreGauge = styled.div<{ score: number }>`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${theme.fontSize.xl};
  font-weight: 800;
  color: ${(p) =>
    p.score >= 60
      ? theme.colors.success
      : p.score >= 30
        ? theme.colors.warning
        : theme.colors.danger};
  background: ${(p) =>
    p.score >= 60
      ? `${theme.colors.success}18`
      : p.score >= 30
        ? `${theme.colors.warning}18`
        : `${theme.colors.danger}18`};
  border: 3px solid
    ${(p) =>
      p.score >= 60
        ? theme.colors.success
        : p.score >= 30
          ? theme.colors.warning
          : theme.colors.danger};
  flex-shrink: 0;
`;

const FactorRow = styled.div`
  font-size: ${theme.fontSize.sm};
  line-height: 1.8;
  word-break: break-word;
  overflow-wrap: break-word;
`;

const RoleBadge = styled(Badge)`
  font-size: 9px;
  padding: 1px 5px;
`;

const TxLink = styled(Link)`
  color: ${theme.colors.primary};
  text-decoration: none;
  font-family: "SF Mono", "Fira Code", monospace;
  font-size: ${theme.fontSize.xs};
  &:hover {
    text-decoration: underline;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing.xl};
  color: ${theme.colors.textMuted};
  font-size: ${theme.fontSize.md};
`;

// ── Helpers ──

function scoreLabel(score: number): string {
  if (score >= 70) return "Good";
  if (score >= 40) return "Fair";
  return "Poor";
}

function impactColor(impact: PrivacyScoreFactor["impact"]): string {
  return impact === "good"
    ? theme.colors.success
    : impact === "bad"
      ? theme.colors.danger
      : theme.colors.textMuted;
}

function impactIcon(impact: PrivacyScoreFactor["impact"]): string {
  return impact === "good" ? "+" : impact === "bad" ? "-" : "~";
}

// ── Component ──

export function MurderBoard() {
  const { selectedNetwork } = useNetworkStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const addressParam = searchParams.get("address") ?? "";
  const [inputValue, setInputValue] = useState(addressParam);
  const { data, isLoading } = useMurderBoard(selectedNetwork, addressParam);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = inputValue.trim();
    if (trimmed) {
      setSearchParams({ address: trimmed });
    }
  };

  return (
    <PageContainer>
      <PageTitle>Murder Board</PageTitle>

      <SearchBar onSubmit={handleSubmit}>
        <AddressInput
          placeholder="Enter an Aztec address (0x...)..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
        />
        <Button type="submit" disabled={!inputValue.trim()}>
          Investigate
        </Button>
      </SearchBar>

      {!addressParam && (
        <EmptyState>
          Enter an address to analyze its public activity fingerprint and privacy exposure.
        </EmptyState>
      )}

      {addressParam && isLoading && <Loading />}

      {addressParam && data && <MurderBoardResults data={data} address={addressParam} />}
    </PageContainer>
  );
}

function MurderBoardResults({
  data,
  address,
}: {
  data: MurderBoardData;
  address: string;
}) {
  const { selectedNetwork } = useNetworkStore();
  const [expandedClusters, setExpandedClusters] = useState<Set<number>>(new Set());
  const [expandedCoAddresses, setExpandedCoAddresses] = useState<Set<string>>(new Set());
  const { data: interactionsData, isLoading: interactionsLoading } = useAddressInteractions(selectedNetwork, address);
  const { data: slotsData, isLoading: slotsLoading } = useRepeatedSlots(selectedNetwork, address);

  const toggleCluster = (clusterId: number) => {
    setExpandedClusters((prev) => {
      const next = new Set(prev);
      if (next.has(clusterId)) next.delete(clusterId);
      else next.add(clusterId);
      return next;
    });
  };

  const toggleCoAddress = (coAddress: string) => {
    setExpandedCoAddresses((prev) => {
      const next = new Set(prev);
      if (next.has(coAddress)) next.delete(coAddress);
      else next.add(coAddress);
      return next;
    });
  };

  if (data.totalTxs === 0) {
    return (
      <EmptyState>
        No public activity found for this address on this network.
      </EmptyState>
    );
  }

  return (
    <>
      {/* Summary row */}
      <Flex gap={theme.spacing.md} align="stretch" wrap style={{ marginBottom: theme.spacing.md }}>
        {/* Privacy score */}
        {data.privacyScore && (
          <Card style={{ flex: "1 1 auto", minWidth: 0, maxWidth: "100%", display: "flex", gap: theme.spacing.md, alignItems: "flex-start", flexWrap: "wrap" }}>
            <ScoreGauge score={data.privacyScore.score}>
              {data.privacyScore.score}
            </ScoreGauge>
            <div style={{ minWidth: 0, flex: "1 1 160px" }}>
              <div style={{ fontWeight: 700, fontSize: theme.fontSize.md, marginBottom: 4 }}>
                Privacy: {scoreLabel(data.privacyScore.score)}
              </div>
              {data.privacyScore.factors.map((f, i) => (
                <FactorRow key={i} style={{ color: theme.colors.textMuted }}>
                  <span style={{ color: impactColor(f.impact), fontWeight: 700 }}>{impactIcon(f.impact)} </span>
                  {f.detail}
                </FactorRow>
              ))}
            </div>
          </Card>
        )}

        {/* Stats */}
        <Grid columns={2} style={{ flex: 1 }}>
          <StatCard>
            <StatValue>{data.totalTxs}</StatValue>
            <StatLabel>Transactions</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{data.clusters.length}</StatValue>
            <StatLabel>Clusters touched</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{data.fpcsUsed.length}</StatValue>
            <StatLabel>Fee payers used</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{interactionsData?.addressInteractions.length ?? "…"}</StatValue>
            <StatLabel>Addresses seen</StatLabel>
          </StatCard>
        </Grid>
      </Flex>

      {/* Cluster distribution */}
      {data.clusters.length > 0 && (
        <>
          <SectionTitle>Cluster Distribution</SectionTitle>
          <Card style={{ marginBottom: theme.spacing.md, padding: 0, overflow: "hidden" }}>
            <TableWrapper>
              <Table style={{ tableLayout: "fixed", width: "100%" }}>
                <colgroup>
                  <col style={{ width: "25%" }} />
                  <col style={{ width: "25%" }} />
                  <col style={{ width: "25%" }} />
                  <col style={{ width: "25%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Cluster</th>
                    <th>Privacy Set Size</th>
                    <th>Your Txs</th>
                    <th>Exposure</th>
                  </tr>
                </thead>
                <tbody>
                  {data.clusters.map((c) => {
                    const pct =
                      c.clusterSize > 0
                        ? ((c.txCount / c.clusterSize) * 100).toFixed(1)
                        : "—";
                    const expanded = expandedClusters.has(c.clusterId);
                    const accentColor = c.clusterId === -1
                      ? theme.colors.danger
                      : theme.colors.cluster[c.clusterId % theme.colors.cluster.length];
                    return (
                      <React.Fragment key={c.clusterId}>
                        <tr
                          style={{ cursor: "pointer" }}
                          onClick={() => toggleCluster(c.clusterId)}
                        >
                          <td>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                              <span style={{
                                fontSize: theme.fontSize.sm,
                                color: accentColor,
                                fontWeight: 700,
                                minWidth: 16,
                                textAlign: "center",
                              }}>
                                {expanded ? "▲" : "▼"}
                              </span>
                              {c.clusterId === -1 ? (
                                <Badge color={theme.colors.danger}>Outlier</Badge>
                              ) : data.latestRunId != null ? (
                                <Link
                                  to={`/${selectedNetwork}/privacy-sets?cluster=${c.clusterId}`}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Badge color={accentColor}>#{c.clusterId}</Badge>
                                </Link>
                              ) : (
                                <Badge color={accentColor}>#{c.clusterId}</Badge>
                              )}
                            </span>
                          </td>
                          <td>{c.clusterSize}</td>
                          <td>{c.txCount}</td>
                          <td>
                            {c.clusterId === -1 ? (
                              <span style={{ color: theme.colors.danger }}>100% — unique</span>
                            ) : (
                              <span
                                style={{
                                  color:
                                    Number(pct) > 50
                                      ? theme.colors.danger
                                      : Number(pct) > 20
                                        ? theme.colors.warning
                                        : theme.colors.success,
                                }}
                              >
                                {pct}% of cluster
                              </span>
                            )}
                          </td>
                        </tr>
                        {expanded && (
                          <tr>
                            <td colSpan={4} style={{ padding: 0, background: `${accentColor}08`, borderTop: `1px solid ${accentColor}22` }}>
                              <ClusterTransactions
                                networkId={selectedNetwork}
                                address={address}
                                clusterId={c.clusterId}
                                latestRunId={data.latestRunId}
                              />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </Table>
            </TableWrapper>
          </Card>
        </>
      )}

      {/* FPCs used */}
      {data.fpcsUsed.length > 0 && (
        <>
          <SectionTitle>Fee Payers Used</SectionTitle>
          <Card style={{ marginBottom: theme.spacing.md }}>
            <TableWrapper>
              <Table style={{ tableLayout: "fixed", width: "100%" }}>
                <colgroup>
                  <col style={{ width: "25%" }} />
                  <col style={{ width: "25%" }} />
                  <col style={{ width: "25%" }} />
                  <col style={{ width: "25%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Address</th>
                    <th>Your Txs</th>
                    <th>Network Share</th>
                    <th>Anonymity</th>
                  </tr>
                </thead>
                <tbody>
                  {data.fpcsUsed.map((fpc) => (
                    <tr key={fpc.address}>
                      <td><HexDisplay address={fpc.address} /></td>
                      <td>{fpc.txCount}</td>
                      <td>
                        <span
                          style={{
                            color:
                              fpc.networkShare > 0.3
                                ? theme.colors.success
                                : fpc.networkShare > 0.1
                                  ? theme.colors.warning
                                  : theme.colors.danger,
                          }}
                        >
                          {(fpc.networkShare * 100).toFixed(1)}%
                        </span>
                      </td>
                      <td>
                        {fpc.isExclusive
                          ? <Badge color={theme.colors.danger} title="You are the only user of this FPC — it provides no anonymity">Exclusive</Badge>
                          : <Badge color={theme.colors.success}>Shared</Badge>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrapper>
          </Card>
        </>
      )}

      {/* Activity heatmap */}
      {data.activityByHour.some((v) => v > 0) && (
        <>
          <SectionTitle>Activity by Hour (UTC)</SectionTitle>
          <Card style={{ marginBottom: theme.spacing.md }}>
            <ActivityHeatmap buckets={data.activityByHour} />
          </Card>
        </>
      )}

      {/* Address interactions */}
      <>
        <SectionTitle>Address Interactions</SectionTitle>
        {interactionsLoading ? (
          <Card style={{ marginBottom: theme.spacing.md }}><Loading /></Card>
        ) : interactionsData && interactionsData.addressInteractions.length > 0 ? (
          <>
            <p style={{ fontSize: theme.fontSize.sm, color: theme.colors.textMuted, marginTop: `-${theme.spacing.sm}`, marginBottom: theme.spacing.sm }}>
              All addresses that appeared in this address's transactions. In Aztec, there are no EOAs —
              user accounts and contracts are both addresses. An address may appear as a{" "}
              <em>callee</em> (contract called), <em>msgSender</em> (caller identity), in{" "}
              <em>calldata</em> (argument passed to a call), as an{" "}
              <em>l2ToL1</em> message recipient/sender, or as the <em>feePayer</em>.
            </p>
            <Card style={{ marginBottom: theme.spacing.md, padding: 0, overflow: "hidden" }}>
              <TableWrapper>
                <Table>
                  <thead>
                    <tr>
                      <th>Address</th>
                      <th>Txs</th>
                      <th>Roles</th>
                      <th>Phases</th>
                    </tr>
                  </thead>
                  <tbody>
                    {interactionsData.addressInteractions.map((ai) => {
                      const expanded = expandedCoAddresses.has(ai.address);
                      return (
                        <React.Fragment key={ai.address}>
                          <tr style={{ cursor: "pointer" }} onClick={() => toggleCoAddress(ai.address)}>
                            <td>
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                                <span style={{
                                  fontSize: theme.fontSize.sm,
                                  color: theme.colors.primary,
                                  fontWeight: 700,
                                  minWidth: 16,
                                  textAlign: "center",
                                }}>
                                  {expanded ? "▲" : "▼"}
                                </span>
                                <HexDisplay address={ai.address} />
                                {ai.label && <Badge>{ai.label}</Badge>}
                                {ai.contractType && <Badge color={theme.colors.textMuted}>{ai.contractType}</Badge>}
                              </span>
                            </td>
                            <td>
                              <span style={{
                                color: ai.coOccurrences / data.totalTxs > 0.5
                                  ? theme.colors.danger
                                  : ai.coOccurrences / data.totalTxs > 0.2
                                    ? theme.colors.warning
                                    : theme.colors.text,
                                fontWeight: ai.coOccurrences / data.totalTxs > 0.5 ? 700 : undefined,
                              }}>
                                {ai.coOccurrences} / {data.totalTxs}
                              </span>
                            </td>
                            <td>
                              <AddressRoleBadges roles={ai.roles} />
                            </td>
                            <td>
                              <Flex gap="4px" wrap>
                                {ai.phases.filter(Boolean).map((ph) => (
                                  <RoleBadge key={ph} color={
                                    ph === "setup" ? theme.colors.accent
                                    : ph === "teardown" ? theme.colors.warning
                                    : theme.colors.primary
                                  }>{ph}</RoleBadge>
                                ))}
                              </Flex>
                            </td>
                          </tr>
                          {expanded && (
                            <tr>
                              <td colSpan={4} style={{ padding: 0, background: `${theme.colors.primary}08`, borderTop: `1px solid ${theme.colors.primary}22` }}>
                                <CoAddressTxs
                                  networkId={selectedNetwork}
                                  address={address}
                                  coAddress={ai.address}
                                />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </Table>
              </TableWrapper>
            </Card>
          </>
        ) : interactionsData ? (
          <Card style={{ marginBottom: theme.spacing.md }}>
            <span style={{ color: theme.colors.textMuted, fontSize: theme.fontSize.sm }}>No address interactions found.</span>
          </Card>
        ) : null}
      </>

      {/* Repeated storage slots */}
      <>
        <SectionTitle>Repeated Storage Writes</SectionTitle>
        {slotsLoading ? (
          <Card style={{ marginBottom: theme.spacing.md }}><Loading /></Card>
        ) : slotsData && slotsData.repeatedSlots.length > 0 ? (
          <Card style={{ marginBottom: theme.spacing.md }}>
            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <th>Slot</th>
                    <th>Contract</th>
                    <th>Txs Writing</th>
                  </tr>
                </thead>
                <tbody>
                  {slotsData.repeatedSlots.map((s) => (
                    <tr key={s.leafSlot}>
                      <td>
                        <span style={{ fontFamily: "monospace", fontSize: theme.fontSize.xs, color: theme.colors.textMuted }}>
                          {s.resolvedLabel ?? s.leafSlot.slice(0, 18) + "…"}
                        </span>
                      </td>
                      <td>
                        {s.contractAddress
                          ? <HexDisplay address={s.contractAddress} />
                          : <span style={{ color: theme.colors.textMuted }}>—</span>
                        }
                      </td>
                      <td>
                        <span style={{ color: s.txCount > 2 ? theme.colors.warning : theme.colors.text }}>
                          {s.txCount}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrapper>
          </Card>
        ) : slotsData ? (
          <Card style={{ marginBottom: theme.spacing.md }}>
            <span style={{ color: theme.colors.textMuted, fontSize: theme.fontSize.sm }}>No repeated storage writes found.</span>
          </Card>
        ) : null}
      </>

    </>
  );
}

function ActivityHeatmap({ buckets }: { buckets: number[] }) {
  const max = Math.max(...buckets, 1);
  const BAR_HEIGHT = 80;
  return (
    <div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: BAR_HEIGHT }}>
        {buckets.map((count, hour) => (
          <div
            key={hour}
            title={`${hour}:00 UTC — ${count} tx${count !== 1 ? "s" : ""}`}
            style={{
              flex: 1,
              height: count === 0 ? 2 : Math.max(4, Math.round((count / max) * BAR_HEIGHT)),
              background: count === 0
                ? theme.colors.border
                : `hsl(${220 - Math.round((count / max) * 120)}, 80%, 55%)`,
              borderRadius: 2,
              alignSelf: "flex-end",
            }}
          />
        ))}
      </div>
      <div style={{ display: "flex", marginTop: 4 }}>
        {buckets.map((_, hour) => (
          <div key={hour} style={{ flex: 1, textAlign: "center" }}>
            {hour % 6 === 0 && (
              <span style={{ fontSize: 9, color: theme.colors.textMuted }}>{hour}h</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function AddressRoleBadges({ roles }: { roles: string[] }) {
  return (
    <Flex gap="4px" wrap>
      {roles.map((r) => (
        <RoleBadge key={r} color={
          r === "callee" ? theme.colors.success
          : r === "msgSender" ? theme.colors.primary
          : r === "feePayer" ? theme.colors.warning
          : r === "calldata" ? theme.colors.accent
          : theme.colors.textMuted
        }>{r}</RoleBadge>
      ))}
    </Flex>
  );
}

function RoleCells({ roles }: { roles: string[] }) {
  return (
    <Flex gap="4px" wrap>
      {roles.map((r, i) => (
        <RoleBadge
          key={i}
          color={
            r === "feePayer"
              ? theme.colors.warning
              : r.includes("calldata")
                ? theme.colors.accent
                : theme.colors.primary
          }
        >
          {r}
        </RoleBadge>
      ))}
    </Flex>
  );
}

function TxSubTable({
  networkId,
  transactions,
  totalPages,
  page,
  onPageChange,
  showCoRoles = false,
}: {
  networkId: string;
  transactions: (MurderBoardTx & { coRoles?: string[] })[];
  totalPages: number;
  page: number;
  onPageChange: (p: number) => void;
  showCoRoles?: boolean;
}) {
  return (
    <div>
      <TableWrapper>
        <Table>
          <thead>
            <tr>
              <th>Block</th>
              <th>Hash</th>
              <th>Your roles</th>
              {showCoRoles && <th>Their roles</th>}
              <th>Status</th>
              <th>Fee Payer</th>
            </tr>
          </thead>
          <tbody>
            {transactions.map((tx) => (
              <tr key={tx.txHash}>
                <td style={{ color: theme.colors.textMuted }}>{tx.blockNumber ?? "—"}</td>
                <td>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                    <AddToClipboardButton hash={tx.txHash} />
                    <TxLink to={`/${networkId}/tx/${tx.txHash}`}>
                      <HexDisplay address={tx.txHash} link={false} />
                    </TxLink>
                  </span>
                </td>
                <td><RoleCells roles={tx.roles} /></td>
                {showCoRoles && <td><RoleCells roles={tx.coRoles ?? []} /></td>}
                <td>
                  <Badge
                    color={
                      tx.status === "finalized"
                        ? theme.colors.success
                        : tx.status === "dropped"
                          ? theme.colors.danger
                          : theme.colors.textMuted
                    }
                  >
                    {tx.status}
                  </Badge>
                </td>
                <td><HexDisplay address={tx.feePayer} /></td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrapper>
      {totalPages > 1 && (
        <Flex gap="12px" style={{ alignItems: "center", justifyContent: "flex-end", padding: theme.spacing.sm }}>
          <Button variant="ghost" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
            Previous
          </Button>
          <span style={{ color: theme.colors.textMuted, fontSize: theme.fontSize.xs }}>
            Page {page} of {totalPages}
          </span>
          <Button variant="ghost" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>
            Next
          </Button>
        </Flex>
      )}
    </div>
  );
}

function ClusterTransactions({
  networkId,
  address,
  clusterId,
  latestRunId,
}: {
  networkId: string;
  address: string;
  clusterId: number;
  latestRunId: number | null;
}) {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useMurderBoard(networkId, address, page, 25, clusterId);

  if (isLoading) return <Loading />;
  if (!data || data.transactions.length === 0) {
    return (
      <div style={{ padding: "12px 16px", color: theme.colors.textMuted, fontSize: theme.fontSize.sm }}>
        No transactions found.
      </div>
    );
  }

  return (
    <TxSubTable
      networkId={networkId}
      transactions={data.transactions}
      totalPages={data.totalPages}
      page={page}
      onPageChange={setPage}
    />
  );
}

function CoAddressTxs({
  networkId,
  address,
  coAddress,
}: {
  networkId: string;
  address: string;
  coAddress: string;
}) {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useCoTxs(networkId, address, coAddress, page, true);

  if (isLoading) return <Loading />;
  if (!data || data.transactions.length === 0) {
    return (
      <div style={{ padding: "12px 16px", color: theme.colors.textMuted, fontSize: theme.fontSize.sm }}>
        No shared transactions found.
      </div>
    );
  }

  return (
    <TxSubTable
      networkId={networkId}
      transactions={data.transactions}
      totalPages={data.totalPages}
      page={page}
      onPageChange={setPage}
      showCoRoles
    />
  );
}
